import { useState } from 'react';
import axios from 'axios';
import { CalendarClock, Send } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { trackEvent } from '@/utils/analytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const initialForm = {
  name: '',
  email: '',
  phone: '',
  preferred_visit_date: '',
  preferred_visit_time: '',
  message: '',
};

const SiteVisitScheduler = ({ project }) => {
  const [formData, setFormData] = useState(initialForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const today = new Date().toISOString().split('T')[0];

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        ...formData,
        project_interest: project.name,
        inquiry_type: 'site_visit',
        message: formData.message || `I would like to schedule a site visit for ${project.name}.`,
      });
      trackEvent('site_visit_submit', { project_id: project.id });
      toast.success('Site visit request saved', { description: 'The Next Leap team will confirm your preferred slot.' });
      setFormData(initialForm);
    } catch (error) {
      toast.error('Could not schedule visit', { description: 'Please try again or use the WhatsApp option.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="site-visit-scheduler" className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8" data-testid="site-visit-scheduler">
      <div className="grid overflow-hidden rounded-[2rem] border border-[#0B1F3B]/10 bg-white shadow-[0_25px_80px_rgba(6,16,30,0.1)] lg:grid-cols-[0.75fr_1.25fr]">
        <div className="blueprint-grid-dark bg-[#0B1F3B] p-8 text-white sm:p-10">
          <div className="mb-5 inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#C5A880] text-[#0B1F3B]"><CalendarClock className="h-5 w-5" /></div>
          <div className="font-tech text-xs uppercase tracking-[0.24em] text-[#C5A880]">Private site visit</div>
          <h2 className="mt-4 font-display text-3xl font-semibold leading-tight sm:text-4xl">Choose your preferred walkthrough window.</h2>
          <p className="mt-5 text-sm leading-relaxed text-white/60">Your request is saved against {project.name} and sent directly to the mandate desk for confirmation.</p>
        </div>

        <form onSubmit={handleSubmit} className="grid gap-4 p-6 sm:grid-cols-2 sm:p-8" data-testid="site-visit-form">
          <label className="grid gap-2 text-sm font-semibold">Full name
            <Input name="name" value={formData.name} onChange={handleChange} required className="h-12 rounded-2xl bg-[#F5F5F5]" data-testid="site-visit-name-input" />
          </label>
          <label className="grid gap-2 text-sm font-semibold">Email
            <Input type="email" name="email" value={formData.email} onChange={handleChange} required className="h-12 rounded-2xl bg-[#F5F5F5]" data-testid="site-visit-email-input" />
          </label>
          <label className="grid gap-2 text-sm font-semibold">Phone / WhatsApp
            <Input name="phone" value={formData.phone} onChange={handleChange} required className="h-12 rounded-2xl bg-[#F5F5F5]" data-testid="site-visit-phone-input" />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="grid gap-2 text-sm font-semibold">Date
              <Input type="date" min={today} name="preferred_visit_date" value={formData.preferred_visit_date} onChange={handleChange} required className="h-12 rounded-2xl bg-[#F5F5F5]" data-testid="site-visit-date-input" />
            </label>
            <label className="grid gap-2 text-sm font-semibold">Time
              <Input type="time" name="preferred_visit_time" value={formData.preferred_visit_time} onChange={handleChange} required className="h-12 rounded-2xl bg-[#F5F5F5]" data-testid="site-visit-time-input" />
            </label>
          </div>
          <label className="grid gap-2 text-sm font-semibold sm:col-span-2">Notes for the visit team
            <Textarea name="message" value={formData.message} onChange={handleChange} rows={4} placeholder="Pickup preference, questions, family members joining..." className="resize-none rounded-2xl bg-[#F5F5F5]" data-testid="site-visit-message-input" />
          </label>
          <Button type="submit" disabled={isSubmitting} className="h-[52px] rounded-full bg-[#0B1F3B] text-white hover:bg-[#06101E] sm:col-span-2" data-testid="site-visit-submit-btn">
            {isSubmitting ? 'Saving visit request...' : 'Request site visit'}
            <Send className="h-4 w-4" />
          </Button>
        </form>
      </div>
    </section>
  );
};

export default SiteVisitScheduler;
